/**
 * Built-in editor plugins for DynamicTableV2 inline editing.
 *
 * Uses the same @wealthlane/ui form components as V1 for visual consistency.
 * Registered automatically by createDefaultRegistry().
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { TextInput } from '@packages/ui/primitives/TextInput';
import { Select } from '@packages/ui/composites/Select';
import type { SelectOption } from '@packages/ui/composites/Select';
import { DatePicker } from '@packages/ui/composites/DatePicker';
import moment from 'moment';
import clsx from 'clsx';
import type { EditorPlugin, EditorRenderProps } from '../types';
import { TABLE_IDS } from '../../semantic-contract';

// ─── Text editor ─────────────────────────────────────────────────────────────

const TextEditor: React.FC<EditorRenderProps> = ({ value, onChange, column, error }) => {
  const [localValue, setLocalValue] = useState(value != null ? String(value) : '');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  useEffect(() => {
    setLocalValue(value != null ? String(value) : '');
  }, [value]);

  return (
    <div style={{ marginBottom: 0, maxWidth: column.editable.width ?? undefined }}>
      <TextInput
        ref={inputRef}
        id={TABLE_IDS.editorText(column.field)}
        type='text'
        value={localValue}
        onChange={(e) => {
          setLocalValue(e.target.value);
          onChange(e.target.value);
        }}
        placeholder={column.editable.placeholder || ''}
        hasError={!!error}
        semanticId={TABLE_IDS.editorText(column.field)}
      />
      {error && <ErrorMessage message={error} />}
    </div>
  );
};

// ─── Number editor ────────────────────────────────────────────────────────────

const NumberEditor: React.FC<EditorRenderProps> = ({ value, onChange, column, error }) => {
  const [localValue, setLocalValue] = useState(value != null ? String(value) : '');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  useEffect(() => {
    setLocalValue(value != null ? String(value) : '');
  }, [value]);

  return (
    <div style={{ marginBottom: 0, maxWidth: column.editable.width ?? undefined }}>
      <TextInput
        ref={inputRef}
        id={TABLE_IDS.editorNumber(column.field)}
        type='number'
        step='any'
        value={localValue}
        onChange={(e) => {
          if (Number(e.target.value) < 0) return;
          setLocalValue(e.target.value);
          const parsed = e.target.value === '' ? null : Number(e.target.value);
          onChange(parsed);
        }}
        placeholder={column.editable.placeholder || ''}
        hasError={!!error}
        semanticId={TABLE_IDS.editorNumber(column.field)}
      />
      {error && <ErrorMessage message={error} />}
    </div>
  );
};

// ─── Date editor ──────────────────────────────────────────────────────────────

const DateEditor: React.FC<EditorRenderProps> = ({ value, onChange, column, error }) => {
  const dateValue = useMemo(() => {
    if (value == null || value === '') return null;
    const m = moment(String(value));
    return m.isValid() ? m.toDate() : null;
  }, [value]);

  return (
    <div style={{ marginBottom: 0, maxWidth: column.editable.width ?? undefined }}>
      <DatePicker
        name={column.field}
        placeholder={column.editable.placeholder || 'MM/DD/YYYY'}
        value={dateValue}
        onChange={(dateVal: Date | null) => {
          if (dateVal) {
            const formatted = moment(dateVal).format('YYYY-MM-DD');
            onChange(formatted);
          } else {
            onChange(null);
          }
        }}
        invalid={!!error}
        portalToBody
        calendarDensity='production'
        semanticId={TABLE_IDS.editorDate(column.field)}
      />
      {error && <ErrorMessage message={error} />}
    </div>
  );
};

// ─── AsyncSelect / Select editor ──────────────────────────────────────────────
// Uses injectedOptions (pre-fetched via useDataInjectionV2) or column.editable.options

const SelectEditor: React.FC<EditorRenderProps> = ({
  value,
  onChange,
  column,
  error,
  injectedOptions,
}) => {
  const isMulti = column.editable.multiple === true;
  const rawOptions =
    injectedOptions && injectedOptions.length > 0 ? injectedOptions : column.editable.options ?? [];

  const selectOptions: SelectOption[] = useMemo(
    () => rawOptions.map((o: any) => ({ label: String(o.label), value: String(o.value) })),
    [rawOptions]
  );

  const currentValue = useMemo(() => {
    if (isMulti) {
      const selectedIds = Array.isArray(value) ? value.map(String) : [];
      return selectOptions.filter((o) => selectedIds.includes(String(o.value)));
    }
    if (value == null || value === '') return null;
    return selectOptions.find((o) => String(o.value) === String(value)) ?? null;
  }, [value, selectOptions, isMulti]);

  return (
    <div style={{ marginBottom: 0, maxWidth: column.editable.width ?? undefined }}>
      <Select
        name={column.field}
        placeholder={column.editable.placeholder || 'Select'}
        value={currentValue}
        onChange={(selected: any) => {
          if (isMulti) {
            const next = Array.isArray(selected) ? selected.map((item) => item?.value ?? item) : [];
            onChange(next);
            return;
          }
          onChange(selected?.value ?? null);
        }}
        options={selectOptions}
        isClearable
        isMulti={isMulti}
        error={!!error}
        semanticId={TABLE_IDS.editorSelect(column.field)}
      />
      {error && <ErrorMessage message={error} />}
    </div>
  );
};

// ─── Shared error component ──────────────────────────────────────────────────

function ErrorMessage({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <div style={{ textAlign: 'left' }}>
      <span className='error-message' style={{ whiteSpace: 'pre-line' }}>{message}</span>
    </div>
  );
}

// ─── Export ───────────────────────────────────────────────────────────────────

export const builtInEditorPlugins: EditorPlugin[] = [
  {
    type: 'text',
    render: (props) => <TextEditor {...props} />,
  },
  {
    type: 'number',
    render: (props) => <NumberEditor {...props} />,
  },
  {
    type: 'date',
    render: (props) => <DateEditor {...props} />,
  },
  {
    type: 'asyncSelect',
    render: (props) => <SelectEditor {...props} />,
  },
  {
    type: 'select',
    render: (props) => <SelectEditor {...props} />,
  },
];
